import React, { useMemo, useState, useEffect } from 'react';
import {
  Loader2, AlertCircle, Inbox, ChevronLeft, ChevronRight, Search,
  X, Bookmark, Mail, Check, ExternalLink, PanelRight,
} from 'lucide-react';
import CompanyLogo from './CompanyLogo';
import CompanyDropdown from './CompanyDropdown';
import ExportAlertsButton from './ExportAlertsButton';
import MonitoringEmptyHint from './MonitoringEmptyHint';
import { severityToRiskTab } from './AlertFiltersBar';
import { applyRecordFilters, sortRecords } from '../utils/recordFilters';
import {
  isWatchlisted, recordKey, relativeTime, formatShortDate, stripMarkdown, getDateFilterLabel,
} from '../utils/format';
import { acknowledgeWatchlistItem } from '../utils/watchlistSnapshots';
import { groupDuplicateStories } from '../utils/storyGrouping';
import { getLastAlertHint } from '../utils/lastAlertHint';
import { displayHeadline } from '../utils/displayHeadline';
import { formatSourceLabel, isVerifiedSource } from '../constants/trustedSources';
import { getRiskLevel } from '../utils/risk';
import { sendAlertBrief } from '../utils/emailActivity';

const PAGE_SIZE = 10;

const CATEGORIES = [
  ['', 'All categories'], ['legal', 'Legal'], ['regulatory', 'Regulatory'], ['hr', 'HR'],
  ['criminal', 'Criminal'], ['financial', 'Financial'], ['compliance', 'Compliance'],
];
const SEVERITIES = [['', 'All severities'], ['high', 'High'], ['medium', 'Medium'], ['low', 'Low']];
const SORTS = [['newest', 'Newest first'], ['oldest', 'Oldest first']];

const SEVERITY_PILL = {
  high: { label: 'High', cls: 'text-[#bd2f2b] bg-[#fae8e7]' },
  medium: { label: 'Medium', cls: 'text-[#86690c] bg-[#f7f0d2]' },
  low: { label: 'Low', cls: 'text-[#117049] bg-[#e1f3ea]' },
  unknown: { label: 'Unrated', cls: 'text-[#4b6679] bg-[#e6edf2]' },
};

const BTN = 'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md border border-[#cbd3df] bg-white text-[13px] font-medium text-[#0d1524] whitespace-nowrap hover:bg-[#f6f7fa] disabled:opacity-40 transition';
const SELECT = 'h-8 text-[13px] font-medium text-[#0d1524] bg-white border border-[#cbd3df] rounded-md px-2.5 pr-7 focus:outline-none focus:border-[#1c4ed8] cursor-pointer hover:bg-[#f6f7fa] transition';

function SeverityBadge({ severity }) {
  const style = SEVERITY_PILL[getRiskLevel(severity)] || SEVERITY_PILL.unknown;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs leading-none font-semibold px-2 py-1.5 rounded whitespace-nowrap ${style.cls}`}>
      <i className="w-1.5 h-1.5 rounded-full bg-current" />
      {style.label}
    </span>
  );
}

function VerifiedLabel({ record }) {
  const ok = isVerifiedSource(record?.source_domain);
  return (
    <span className={`inline-flex items-center gap-1 text-[11.5px] ${ok ? 'text-[#117049]' : 'text-[#b05a0a]'}`}>
      {ok ? <Check size={11} /> : <X size={11} />}
      {ok ? 'Verified source' : 'Unverified source'}
    </span>
  );
}

function categoryLabel(record) {
  const cat = record?.categories?.[0];
  return cat ? cat.charAt(0).toUpperCase() + cat.slice(1) : 'Uncategorised';
}

function StoryRow({ group, selected, onSelect, onOpen }) {
  const { primary: record, sourceCount } = group;
  const high = getRiskLevel(record.severity) === 'high';
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(group)}
      onDoubleClick={() => onOpen(group)}
      onKeyDown={e => {
        if (e.key === 'Enter' && e.shiftKey) { e.preventDefault(); onOpen(group); }
        else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(group); }
      }}
      className={`grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_72px_84px] gap-2 md:gap-3 items-start md:items-center px-3.5 py-3 border-t border-[#e1e5ec] border-l-[3px] cursor-pointer md:min-w-[480px] hover:bg-[#f6f7fa] ${
        selected ? '!bg-[#e7edfc] border-l-[#1c4ed8]' : high ? 'border-l-[#bd2f2b]' : 'border-l-transparent'
      }`}
    >
      <span className="min-w-0">
        <span className="flex items-center gap-2 min-w-0">
          <CompanyLogo name={record.company} size="sm" />
          <span className="truncate text-[13.5px] font-semibold text-[#1c4ed8]">{record.company}</span>
          <span className="flex-none text-xs font-medium text-[#45516a] bg-[#f6f7fa] border border-[#e1e5ec] rounded px-1.5 py-0.5">
            {categoryLabel(record)}
          </span>
        </span>
        <b
          role="link"
          tabIndex={-1}
          title="Open full story"
          onClick={(e) => { e.stopPropagation(); onOpen(group); }}
          className="block mt-1 mb-0.5 text-sm font-semibold leading-snug text-[#0d1524] line-clamp-2 hover:text-[#1c4ed8] hover:underline underline-offset-2 cursor-pointer"
        >
          {displayHeadline(record)}
        </b>
        <span className="flex items-center gap-2.5">
          <VerifiedLabel record={record} />
          {sourceCount > 1 && <span className="text-[11.5px] text-[#76829a]">{sourceCount} sources</span>}
        </span>
      </span>
      <span className="flex items-center justify-between gap-3 md:contents">
        <SeverityBadge severity={record.severity} />
        <span className="md:text-right text-[13px] font-medium text-[#45516a]">
          {record.published_at ? relativeTime(record.published_at) : '—'}
          <small className="ml-2 md:ml-0 md:block text-[11px] font-normal text-[#76829a]">
            {record.published_at ? formatShortDate(record.published_at) : ''}
          </small>
        </span>
      </span>
    </div>
  );
}

function DetailPanel({ group, watchlist, toggleWatchlist, onEmailSent, onOpen, onClose }) {
  const [briefState, setBriefState] = useState('idle');
  const record = group.primary;
  const saved = isWatchlisted(record, watchlist);
  const summary = stripMarkdown(record.summary || record.snippet || record.content || '');
  const verified = isVerifiedSource(record.source_domain);

  useEffect(() => { setBriefState('idle'); }, [record]);

  const handleSendBrief = async () => {
    if (briefState === 'sending') return;
    setBriefState('sending');
    try {
      const activity = await sendAlertBrief(record);
      setBriefState(activity.status === 'failed' ? 'failed' : 'sent');
      onEmailSent?.(activity);
    } catch {
      setBriefState('failed');
    }
    setTimeout(() => setBriefState('idle'), 2500);
  };

  return (
    <aside className="bg-white border border-[#e1e5ec] rounded-lg p-[18px] xl:sticky xl:top-[70px]">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-[#5b6780]">Selected story</span>
        <button type="button" onClick={onClose} aria-label="Close"
          className="w-[26px] h-[26px] grid place-items-center rounded-md text-[#45516a] hover:bg-[#f6f7fa]">
          <X size={14} />
        </button>
      </div>
      <div className="flex items-center gap-1.5 mt-3">
        <SeverityBadge severity={record.severity} />
        <span className="text-xs font-medium px-2 py-1.5 leading-none rounded border border-[#e1e5ec] bg-[#f6f7fa] text-[#45516a]">
          {categoryLabel(record)}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <CompanyLogo name={record.company} size="sm" />
        <span className="text-sm font-semibold text-[#1c4ed8]">{record.company}</span>
      </div>
      <h2 className="my-3 text-[19px] leading-[1.22] font-semibold text-[#0d1524]">{displayHeadline(record)}</h2>
      <VerifiedLabel record={record} />

      <div className="mt-5 pt-4 border-t border-[#e1e5ec] flex items-center gap-2 text-sm font-semibold text-[#0d1524]">
        <span className="w-1 h-4 rounded-full bg-[#1c4ed8]" />What we know
      </div>
      <p className="mt-2 text-[13.5px] leading-relaxed text-[#5b6780] line-clamp-6">
        {summary || 'No summary yet. Open the full intelligence to read the source.'}
      </p>

      <div className="mt-3 text-xs text-[#45516a]">
        <b className="font-medium">{formatSourceLabel(record.source_domain) || 'Source'}</b>
        <br />
        <span className="text-[11.5px] text-[#76829a]">
          {record.published_at ? formatShortDate(record.published_at) : 'Date unknown'}
        </span>
        {record.url && (
          <a href={record.url} target="_blank" rel="noopener noreferrer"
            className="ml-2 inline-flex items-center gap-1 text-[11.5px] text-[#1c4ed8] hover:underline">
            <ExternalLink size={11} />Original
          </a>
        )}
      </div>

      {!verified && (
        <div className="mt-3 rounded-md bg-[#fbeddb] px-3 py-2.5 text-[12.5px] text-[#0d1524]">
          Single unverified source. Treat as unconfirmed.
        </div>
      )}

      <button type="button" onClick={() => onOpen(group)}
        className="mt-4 w-full h-8 rounded-md bg-[#1c4ed8] text-[13px] font-medium text-white hover:brightness-110 transition">
        Open full intelligence
      </button>
      <div className="flex gap-2 mt-2">
        <button type="button" onClick={() => toggleWatchlist(recordKey(record))}
          className={`${BTN} flex-1 ${saved ? '!bg-[#e7edfc] !border-[#1c4ed8] !text-[#1c4ed8]' : ''}`}>
          <Bookmark size={14} fill={saved ? 'currentColor' : 'none'} />{saved ? 'Saved' : 'Save'}
        </button>
        <button type="button" onClick={handleSendBrief} disabled={briefState === 'sending'} className={`${BTN} flex-1`}>
          {briefState === 'sending' ? <><Loader2 size={14} className="animate-spin" />Sending…</>
            : briefState === 'sent' ? <><Check size={14} className="text-[#117049]" />Sent</>
              : briefState === 'failed' ? <><AlertCircle size={14} className="text-[#bd2f2b]" />Failed</>
                : <><Mail size={14} />Send brief</>}
        </button>
      </div>
    </aside>
  );
}

const TABS = [['all', 'All stories'], ['high', 'High risk'], ['saved', 'Saved']];

function IntelligenceView({
  records, loading, error,
  companies, activeCompanies, toggleCompany,
  categoryFilter, setCategoryFilter, severityFilter, setSeverityFilter,
  watchlist, toggleWatchlist, updatedWatchlistIds, onWatchlistAcknowledge,
  dateFilter, sortOrder, setSortOrder,
  onEmailSent,
  onOpenRecord,
}) {
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [tab, setTab] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [panelOpen, setPanelOpen] = useState(true);

  const companyOptions = useMemo(() => {
    const names = new Set((companies || []).map(c => c.name || c).filter(Boolean));
    records
      .filter(record => record.entity_type === 'person')
      .forEach(record => { if (record.company) names.add(record.company); });
    if (names.size === 0) records.forEach(record => { if (record.company) names.add(record.company); });
    return [...names].sort((a, b) => a.localeCompare(b)).map(name => ({ name }));
  }, [companies, records]);

  const filteredRecords = useMemo(() => sortRecords(
    applyRecordFilters(records, {
      dateFilter, activeCompanies, categoryFilter, severityFilter, searchText,
    }),
    sortOrder,
  ), [records, activeCompanies, categoryFilter, severityFilter, sortOrder, dateFilter, searchText]);

  const groupedStories = useMemo(() => groupDuplicateStories(filteredRecords), [filteredRecords]);

  const tabCounts = useMemo(() => ({
    all: groupedStories.length,
    high: groupedStories.filter(g => getRiskLevel(g.primary.severity) === 'high').length,
    saved: groupedStories.filter(g => isWatchlisted(g.primary, watchlist)).length,
  }), [groupedStories, watchlist]);

  const tabStories = useMemo(() => {
    if (tab === 'high') return groupedStories.filter(g => getRiskLevel(g.primary.severity) === 'high');
    if (tab === 'saved') return groupedStories.filter(g => isWatchlisted(g.primary, watchlist));
    return groupedStories;
  }, [groupedStories, tab, watchlist]);

  const lastAlertHint = useMemo(() => getLastAlertHint(records), [records]);

  const exportFilterLabel = useMemo(() => {
    const parts = [getDateFilterLabel(dateFilter)];
    if (activeCompanies.length > 0) parts.push(activeCompanies.join(', '));
    if (categoryFilter) parts.push(categoryFilter);
    if (severityFilter) parts.push(`${severityFilter} risk`);
    return parts.filter(Boolean).join(' · ');
  }, [dateFilter, activeCompanies, categoryFilter, severityFilter]);

  useEffect(() => { setPage(1); }, [activeCompanies, categoryFilter, severityFilter, sortOrder, dateFilter, searchText, tab]);

  const totalPages = Math.max(1, Math.ceil(tabStories.length / PAGE_SIZE));
  const visibleGroups = useMemo(
    () => tabStories.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [tabStories, page],
  );

  // Selection follows the list: fall back to the first row when the selected story is filtered out.
  const selectedGroup = useMemo(
    () => tabStories.find(g => recordKey(g.primary) === selectedId) || tabStories[0] || null,
    [tabStories, selectedId],
  );

  const dirty = Boolean(
    searchText || activeCompanies.length || categoryFilter || severityFilter || tab !== 'all',
  );
  const resetFilters = () => {
    setSearchText('');
    setCategoryFilter('');
    setSeverityFilter('');
    setTab('all');
    activeCompanies.forEach(toggleCompany);
  };

  const handleOpen = (group) => {
    const record = group?.primary;
    if (!record) return;
    if (isWatchlisted(record, watchlist)) {
      acknowledgeWatchlistItem(record);
      onWatchlistAcknowledge?.();
    }
    onOpenRecord?.(record, {
      relatedRecords: group.related,
      sequence: tabStories.map(g => g.primary),
    });
  };

  const showPanel = panelOpen && selectedGroup;
  const from = tabStories.length > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const to = Math.min(page * PAGE_SIZE, tabStories.length);

  return (
    <>
      {/* Page header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl leading-tight font-semibold tracking-tight text-[#0d1524]">Triage the signal.</h1>
          <p className="mt-1 text-sm text-[#45516a]">
            {loading ? 'Loading stories…' : `${tabCounts.all} ${tabCounts.all === 1 ? 'story' : 'stories'} · ${getDateFilterLabel(dateFilter)}`}
          </p>
        </div>
        <ExportAlertsButton records={filteredRecords} filterLabel={exportFilterLabel} disabled={loading} />
      </div>

      {/* Filters (z-20 keeps the company dropdown above the table) */}
      <div className="relative z-20 bg-white border border-[#e1e5ec] rounded-lg p-3.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#76829a] pointer-events-none" />
            <input
              type="text"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search by company, person or keyword"
              className="h-8 w-full text-[13px] text-[#0d1524] bg-white border border-[#cbd3df] rounded-md pl-9 pr-8 focus:outline-none focus:border-[#1c4ed8] placeholder:text-[#76829a]"
            />
            {searchText && (
              <button type="button" onClick={() => setSearchText('')} aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#76829a] hover:text-[#0d1524]">
                <X size={14} />
              </button>
            )}
          </div>
          <CompanyDropdown companies={companyOptions} activeCompanies={activeCompanies} toggleCompany={toggleCompany} />
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className={SELECT} aria-label="Category">
            {CATEGORIES.map(([v, l]) => <option key={v || 'all'} value={v}>{l}</option>)}
          </select>
          <select
            value={severityToRiskTab(severityFilter) === 'all' ? '' : severityToRiskTab(severityFilter)}
            onChange={e => setSeverityFilter(e.target.value)}
            className={SELECT}
            aria-label="Severity"
          >
            {SEVERITIES.map(([v, l]) => <option key={v || 'all'} value={v}>{l}</option>)}
          </select>
          {dirty && (
            <button type="button" onClick={resetFilters} className="text-[12.5px] text-[#1c4ed8] hover:underline">Reset</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div role="tablist" className="flex items-end gap-[22px] border-b border-[#e1e5ec] mt-[18px] overflow-x-auto">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-[7px] py-2.5 -mb-px text-[13px] whitespace-nowrap border-b-2 ${
              tab === key ? 'text-[#1c4ed8] border-[#1c4ed8] font-medium' : 'text-[#45516a] border-transparent hover:text-[#0d1524]'
            }`}
          >
            {label}
            <em className="not-italic text-[11px] font-medium bg-[#f6f7fa] border border-[#e1e5ec] rounded px-[5px] py-px text-[#45516a]">
              {tabCounts[key]}
            </em>
          </button>
        ))}
        <span className="flex-1" />
        <span className="flex items-center gap-1.5 pb-1.5">
          <span className="text-xs text-[#5b6780]">Sort by</span>
          <select value={sortOrder} onChange={e => setSortOrder(e.target.value)} className={SELECT} aria-label="Sort">
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button
            type="button"
            onClick={() => setPanelOpen(o => !o)}
            aria-label={panelOpen ? 'Hide detail panel' : 'Show detail panel'}
            aria-pressed={panelOpen}
            title={panelOpen ? 'Hide detail panel' : 'Show detail panel'}
            className={`hidden xl:grid w-8 h-8 place-items-center rounded-md border transition ${
              panelOpen ? 'border-[#1c4ed8] bg-[#e7edfc] text-[#1c4ed8]' : 'border-[#cbd3df] bg-white text-[#45516a] hover:bg-[#f6f7fa]'
            }`}
          >
            <PanelRight size={15} />
          </button>
        </span>
      </div>

      <div className={`grid gap-4 mt-3.5 items-start ${showPanel ? 'xl:grid-cols-[minmax(0,1fr)_340px]' : 'grid-cols-1'}`}>
        <div className="min-w-0">
          <div className="bg-white border border-[#e1e5ec] rounded-lg overflow-x-auto">
            <div className="hidden md:grid grid-cols-[minmax(0,1fr)_72px_84px] gap-3 px-3.5 py-2.5 pl-[17px] bg-[#f6f7fa] text-xs font-medium text-[#5b6780] min-w-[480px]">
              <span>Story · {tabStories.length} results</span>
              <span>Severity</span>
              <span className="text-right">Published</span>
            </div>

            {loading ? (
              <div className="text-center py-16 text-[#76829a] border-t border-[#e1e5ec]">
                <Loader2 size={26} className="animate-spin mx-auto" />
                <p className="mt-2 text-sm">Loading stories…</p>
              </div>
            ) : error ? (
              <div className="text-center py-16 text-[#bd2f2b] border-t border-[#e1e5ec]">
                <AlertCircle size={26} className="mx-auto" />
                <p className="mt-2 text-sm">{error}</p>
              </div>
            ) : tabStories.length === 0 ? (
              <div className="text-center py-14 px-6 text-[#76829a] border-t border-[#e1e5ec]">
                <Inbox size={26} className="mx-auto" />
                <p className="mt-2 text-sm font-medium text-[#0d1524]">
                  {tab === 'saved' ? 'Nothing saved yet' : 'No stories match these filters'}
                </p>
                {tab === 'all' && (
                  <MonitoringEmptyHint
                    hint={lastAlertHint}
                    subtitle="Widen the date range or loosen a filter to see more."
                  />
                )}
                {dirty && (
                  <button type="button" onClick={resetFilters} className={`${BTN} mt-3`}>Reset all filters</button>
                )}
              </div>
            ) : visibleGroups.map(group => {
              const id = recordKey(group.primary);
              return (
                <div key={id} className={updatedWatchlistIds?.has(id) ? 'bg-[#fbeddb]/40' : ''}>
                  <StoryRow
                    group={group}
                    selected={selectedGroup && recordKey(selectedGroup.primary) === id}
                    onSelect={(g) => { setSelectedId(recordKey(g.primary)); setPanelOpen(true); }}
                    onOpen={handleOpen}
                  />
                </div>
              );
            })}

            {/* Pagination */}
            <div className="flex flex-wrap items-center justify-between gap-2.5 px-3.5 py-2.5 border-t border-[#e1e5ec] text-xs text-[#45516a]">
              <span>Showing {from}–{to} of {tabStories.length} stories</span>
              <div className="flex gap-1">
                <button type="button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                  className="min-w-[28px] h-7 grid place-items-center rounded-[5px] disabled:opacity-35" aria-label="Previous page">
                  <ChevronLeft size={13} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(n => totalPages <= 7 || n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                  .map((n, i, arr) => (
                    <React.Fragment key={n}>
                      {i > 0 && n - arr[i - 1] > 1 && <span className="px-1 self-center">…</span>}
                      <button type="button" onClick={() => setPage(n)}
                        className={`min-w-[28px] h-7 rounded-[5px] border text-xs ${
                          n === page ? 'border-[#cbd3df] bg-[#f6f7fa] font-semibold' : 'border-transparent hover:bg-[#f6f7fa]'
                        }`}>
                        {n}
                      </button>
                    </React.Fragment>
                  ))}
                <button type="button" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                  className="min-w-[28px] h-7 grid place-items-center rounded-[5px] disabled:opacity-35" aria-label="Next page">
                  <ChevronRight size={13} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {showPanel && (
          <DetailPanel
            group={selectedGroup}
            watchlist={watchlist}
            toggleWatchlist={toggleWatchlist}
            onEmailSent={onEmailSent}
            onOpen={handleOpen}
            onClose={() => setPanelOpen(false)}
          />
        )}
      </div>
    </>
  );
}

export default IntelligenceView;
